import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subDays, subHours } from "date-fns";
import { RealtimeChannel } from "@supabase/supabase-js";

interface NotificationStats {
  status: string;
  count: number;
  priority: string;
}

interface NotificationVolumeData {
  time: string;
  count: number;
  resolved: number;
  pending: number;
}

export const useRealtimeNotifications = (timeRange: string, refreshInterval: number) => {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  // Parse time range
  const getTimeFilter = () => {
    const now = new Date();
    switch (timeRange) {
      case "1h":
        return subHours(now, 1);
      case "4h":
        return subHours(now, 4);
      case "24h":
        return subDays(now, 1);
      case "7d":
        return subDays(now, 7);
      case "30d":
        return subDays(now, 30);
      default:
        return subDays(now, 1);
    }
  };

  // Notification status stats
  const { data: statusStats, refetch: refetchStatus } = useQuery({
    queryKey: ["notification-stats", timeRange],
    queryFn: async (): Promise<NotificationStats[]> => {
      const timeFilter = getTimeFilter();
      
      const { data, error } = await supabase
        .from("notifications")
        .select("status, priority")
        .gte("created_at", timeFilter.toISOString())
        .eq("is_deleted", false);

      if (error) throw error;

      const statusGroups = data.reduce((acc: Record<string, NotificationStats[]>, item) => {
        if (!acc[item.status]) {
          acc[item.status] = [];
        }
        acc[item.status].push({
          status: item.status,
          count: 1,
          priority: item.priority
        });
        return acc;
      }, {});

      return Object.entries(statusGroups).map(([status, items]) => ({
        status,
        count: items.length,
        priority: items[0]?.priority || "middel"
      }));
    },
    refetchInterval: refreshInterval * 1000,
  });

  // Notification volume over time
  const { data: volumeData, refetch: refetchVolume } = useQuery({
    queryKey: ["notification-volume", timeRange],
    queryFn: async (): Promise<NotificationVolumeData[]> => {
      const timeFilter = getTimeFilter();
      
      const { data, error } = await supabase
        .from("notifications")
        .select("created_at, status, updated_at")
        .gte("created_at", timeFilter.toISOString())
        .eq("is_deleted", false)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const getTimeInterval = () => {
        switch (timeRange) {
          case "1h":
          case "4h":
            return 15;
          case "24h":
            return 60;
          case "7d":
            return 1440;
          case "30d":
            return 1440;
          default:
            return 60;
        }
      };

      const intervalMinutes = getTimeInterval();
      const intervals: Record<string, NotificationVolumeData> = {};

      data.forEach((notification) => {
        const createdTime = new Date(notification.created_at);
        const intervalStart = new Date(
          Math.floor(createdTime.getTime() / (intervalMinutes * 60 * 1000)) * intervalMinutes * 60 * 1000
        );
        
        const timeKey = intervalStart.toISOString();
        
        if (!intervals[timeKey]) {
          intervals[timeKey] = {
            time: intervalStart.toLocaleTimeString('nl-NL', { 
              hour: '2-digit', 
              minute: '2-digit' 
            }),
            count: 0,
            resolved: 0,
            pending: 0
          };
        }
        
        intervals[timeKey].count++;
        
        if (notification.status === 'gesloten' || notification.status === 'opgelost') {
          intervals[timeKey].resolved++;
        } else {
          intervals[timeKey].pending++;
        }
      });

      return Object.values(intervals).sort((a, b) => 
        new Date(`2000-01-01 ${a.time}`).getTime() - new Date(`2000-01-01 ${b.time}`).getTime()
      );
    },
    refetchInterval: refreshInterval * 1000,
  });

  const cleanupChannel = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  const setupChannel = useCallback(() => {
    cleanupChannel();
    retryCountRef.current = 0;

    console.log('[Realtime] Setting up dashboard notifications channel');

    const channel = supabase
      .channel('notifications-dashboard-stats')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        () => {
          refetchStatus();
          refetchVolume();
        }
      )
      .subscribe((status) => {
        console.log(`[Realtime] Dashboard channel status: ${status}`);
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
          retryCountRef.current++;
          console.log(`[Realtime] Dashboard reconnecting in ${delay}ms (attempt ${retryCountRef.current})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            setupChannel();
            refetchStatus();
            refetchVolume();
          }, delay);
        }
        if (status === 'SUBSCRIBED') {
          retryCountRef.current = 0;
        }
      });

    channelRef.current = channel;
  }, [refetchStatus, refetchVolume, cleanupChannel]);

  useEffect(() => {
    setupChannel();

    const handleSessionRestored = () => {
      console.log('[Realtime] Session restored, rebuilding dashboard channel');
      setupChannel();
      refetchStatus();
      refetchVolume();
    };

    window.addEventListener('session-restored', handleSessionRestored);

    return () => {
      window.removeEventListener('session-restored', handleSessionRestored);
      cleanupChannel();
    };
  }, [setupChannel, refetchStatus, refetchVolume, cleanupChannel]);

  return {
    statusStats: statusStats || [],
    volumeData: volumeData || [],
    isLoading: !statusStats || !volumeData
  };
};
